import * as React from 'react';
import { useDiagnosisPermission } from '../../../contexts/DiagnosisPermissionContext';
import { forwardRef, useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  Stack,
  Typography,
  Box,
  Tabs,
  Tab,
  Grid,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  CircularProgress,
  IconButton,
  Tooltip,
  Paper,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import toast from 'react-hot-toast';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import TableRowsIcon from '@mui/icons-material/TableRows';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { ReadOnlyDataGrid, ReadOnlyField, RowAction } from '../../ReadOnlyDataGrid';
import {
  DiagnosisEnergyUseReadDTO,
  DiagnosisEnergyUseSettingsReadDTO,
  DiagnosisEnergyUseSettingsCreateDTO,
  IDiagnosisEnergyUseSettingsCreateDTO,
  DiagnosisEnergyUseVectorDataReadDTO,
  DiagnosisEnergyUseTotalsDataReadDTO,
  DiagnosisEnergyVectorsReadDTO,
  DiagnosisReadDTO,
  EnergyUseReadDTO,
} from '../../../generated/api-client';
import { apiClient } from '../../../services/apiClient';
import { DiagnosisStepHandle } from './Step.types';
import { useDiagnosisEnergyUseByDiagnosisId } from '../../../hooks';
import { useVectorTypeOptions } from '../../../hooks/useVectorTypeOptions';
import { formatNumberItalian } from '../../../utils/numberFormat';
import { DECIMAL_PLACES } from '../../../constants/decimalPlacesPolicy';
import { EditEnergyUseDialog } from './EditEnergyUseDialog';
import { GridRowId } from '@mui/x-data-grid';
import { ConfirmDialog } from '../../common/ConfirmDialog';
import { ActionSpacer } from '../../common';
import { AiSearchModal } from '../../search/AiSearchModal';
import { fetchAiRecommender, type AiSearchResultItem } from '../../../services/aiSearchService';
import { aiFonteFromItem, aiFonteReadOnlyField } from '../../../utils/aiFonteDisplay';
import { importAiItemToDiagnosis, resolveAiEnergyUseRow } from '../../../utils/aiDiagnosisImport';
import { PieChartWrapper, pieArcLabelClasses } from '../../charts';
import { formatUnitOfMeasurement } from '../../../utils/unitOfMeasurementFormatter';

interface DiagnosisEnergyUseStepProps {
  diagnosisId?: string;
}

type ViewMode = 'table' | 'charts';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface ChartDatum {
  id: string;
  label: string;
  value: number;
  percentage: number;
  color: string;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`energy-use-tabpanel-${index}`}
      aria-labelledby={`energy-use-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

// Color palette for energy vectors - highly distinct colors
const vectorColors = [
  '#FF1744', // Rosso intenso
  '#00E676', // Verde brillante
  '#2979FF', // Blu elettrico
  '#FF6D00', // Arancione acceso
  '#AA00FF', // Viola intenso
  '#00E5FF', // Ciano brillante
  '#FFD600', // Giallo oro
  '#64FFDA', // Turchese
];

// Color palette for energy uses - highly distinct colors, maximum separation
const energyUseColors = [
  '#E91E63', // Rosa fucsia
  '#00BCD4', // Ciano
  '#4CAF50', // Verde
  '#FF9800', // Arancione
  '#9C27B0', // Viola
  '#F44336', // Rosso
  '#2196F3', // Blu
  '#FFEB3B', // Giallo
  '#FF5722', // Arancione scuro
  '#009688', // Teal
  '#3F51B5', // Indaco
  '#795548', // Marrone
  '#607D8B', // Blu grigio
  '#FFC107', // Ambra
  '#8BC34A', // Verde chiaro
  '#FF4081', // Rosa
  '#00ACC1', // Ciano scuro
  '#7C4DFF', // Viola chiaro
  '#FF6F00', // Arancione scuro
  '#00E676', // Verde lime
  '#1DE9B6', // Turchese chiaro
  '#D500F9', // Magenta
  '#FF1744', // Rosso acceso
  '#00B8D4', // Ciano medio
  '#76FF03', // Verde lime brillante
  '#FF9100', // Arancione
  '#651FFF', // Viola scuro
  '#00BFA5', // Teal chiaro
  '#FF3D00', // Rosso arancione
  '#2962FF', // Blu intenso
  '#C6FF00', // Giallo verde
  '#FF00C8', // Rosa magenta
  '#00E5FF', // Ciano brillante
  '#FFD740', // Giallo ambra
  '#18FFFF', // Ciano chiaro
  '#FF5252', // Rosso chiaro
  '#69F0AE', // Verde acqua
  '#7C4DFF', // Viola medio
  '#FFAB40', // Arancione chiaro
  '#00E676', // Verde brillante
];

export const DiagnosisEnergyUseStep = React.memo(
  forwardRef<DiagnosisStepHandle, DiagnosisEnergyUseStepProps>(({ diagnosisId }, ref) => {
    const queryClient = useQueryClient();
    const [tabValue, setTabValue] = useState(0);
    const [viewMode, setViewMode] = useState<ViewMode>('table');
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedEnergyUse, setSelectedEnergyUse] = useState<DiagnosisEnergyUseReadDTO | null>(
      null
    );
    const [deleteDialogState, setDeleteDialogState] = useState<{
      open: boolean;
      id: string | null;
    }>({ open: false, id: null });
    const [settingsFormData, setSettingsFormData] =
      useState<IDiagnosisEnergyUseSettingsCreateDTO | null>(null);
    const [aiSearchOpen, setAiSearchOpen] = useState(false);

    const { data: energyUse = [], isLoading } = useDiagnosisEnergyUseByDiagnosisId(diagnosisId);

    const { data: previousDiagnosis, isLoading: isLoadingPreviousDiagnosis } =
      useQuery<DiagnosisReadDTO | null>(
        ['diagnosis-previous-for-same-base-site', diagnosisId],
        () =>
          apiClient.get_previous_diagnosis_for_same_base_site_diagnosis__diagnosis_id__previous_for_same_base_site_get(
            diagnosisId!
          ),
        { enabled: !!diagnosisId }
      );
    const previousDiagnosisId = previousDiagnosis?.id;
    const { data: previousEnergyUses = [], isLoading: isLoadingPreviousUses } =
      useDiagnosisEnergyUseByDiagnosisId(previousDiagnosisId);

    const { data: catalogEnergyUses = [], isLoading: isLoadingCatalogEnergyUses } = useQuery<
      EnergyUseReadDTO[]
    >('energy-use-catalog-all', () => apiClient.list_items_energy_use__get(), {
      staleTime: 5 * 60 * 1000,
    });

    type AiEnergyUseBundle = { item: AiSearchResultItem; row: DiagnosisEnergyUseReadDTO };

    const { data: aiEnergyUseBundles = [], isLoading: isLoadingAiEnergyUses } = useQuery<
      AiEnergyUseBundle[]
    >(
      [
        'ai-recommender-energy-uses',
        diagnosisId,
        energyUse
          .map(u => (u.code || '').trim())
          .filter(Boolean)
          .join('|'),
      ],
      async () => {
        const items = await fetchAiRecommender(diagnosisId!, 'energy_use');
        const existingCodes = new Set(energyUse.map(u => (u.code || '').trim()).filter(Boolean));
        const filtered = items.filter(it => {
          const c = String(it.payload.code ?? '').trim();
          return !c || !existingCodes.has(c);
        });
        return filtered.map(item => ({
          item,
          row: {
            id: item.id_source,
            ...item.payload,
            fonte: aiFonteFromItem(item),
          } as any,
        }));
      },
      { enabled: !!diagnosisId, keepPreviousData: true }
    );

    const aiSuggestedEnergyUses = useMemo(
      () => aiEnergyUseBundles.map(b => b.row),
      [aiEnergyUseBundles]
    );

    const currentEnergyUseCodes = useMemo(
      () => new Set(energyUse.map(u => (u.code || '').trim()).filter(Boolean)),
      [energyUse]
    );

    const previousEnergyUsesFiltered = useMemo(
      () =>
        previousEnergyUses.filter(u => {
          const c = (u.code || '').trim();
          return !c || !currentEnergyUseCodes.has(c);
        }),
      [previousEnergyUses, currentEnergyUseCodes]
    );

    // Bug 12: il catalogo standard mostra sempre tutte le voci (es. 26), anche quelle già
    // presenti nella diagnosi attuale: queste ultime vengono marcate con un badge "Già presente"
    // e non sono re-importabili (vedi `isCatalogEnergyUseAlreadyPresent` e
    // `catalogEnergyUseImportField` più sotto), invece di essere nascoste dalla lista.
    const isCatalogEnergyUseAlreadyPresent = useCallback(
      (row: EnergyUseReadDTO) => {
        if (!row?.id) return false;
        const codeNorm = (row.code || '').trim();
        if (codeNorm && currentEnergyUseCodes.has(codeNorm)) return true;
        return energyUse.some(u => u.original_energy_use_id === row.id);
      },
      [currentEnergyUseCodes, energyUse]
    );

    const aiEnergyUseItemByRowId = useMemo(() => {
      const m = new Map<string, AiSearchResultItem>();
      aiEnergyUseBundles.forEach(b => {
        if (b.row.id) m.set(b.row.id, b.item);
      });
      return m;
    }, [aiEnergyUseBundles]);

    // Carica i dati per la prima tab
    const { data: vectorData = [], isLoading: isLoadingVectorData } = useQuery<
      DiagnosisEnergyUseVectorDataReadDTO[]
    >(
      `diagnosis-energy-use-vector-data-${diagnosisId}`,
      () =>
        apiClient.search_by_diagnosis_id_diagnosis_energy_use_vector_data_search_by_diagnosis_id__diagnosis_id__get(
          diagnosisId!
        ),
      { enabled: !!diagnosisId }
    );

    const { data: totalsData = [], isLoading: isLoadingTotalsData } = useQuery<
      DiagnosisEnergyUseTotalsDataReadDTO[]
    >(
      `diagnosis-energy-use-totals-data-${diagnosisId}`,
      () =>
        apiClient.search_by_diagnosis_id_diagnosis_energy_use_totals_data_search_by_diagnosis_id__diagnosis_id__get(
          diagnosisId!
        ),
      { enabled: !!diagnosisId }
    );

    const { data: vectors = [], isLoading: isLoadingVectors } = useQuery<
      DiagnosisEnergyVectorsReadDTO[]
    >(
      `diagnosis-energy-vectors-${diagnosisId}`,
      () =>
        apiClient.search_by_diagnosis_id_diagnosis_energy_vectors_search_by_diagnosis_id__diagnosis_id__get(
          diagnosisId!
        ),
      { enabled: !!diagnosisId }
    );

    const { data: vectorTypeOptions = [] } = useVectorTypeOptions();

    const { data: settings, isLoading: isLoadingSettings } =
      useQuery<DiagnosisEnergyUseSettingsReadDTO>(
        `diagnosis-energy-use-settings-${diagnosisId}`,
        () =>
          apiClient.search_by_diagnosis_id_diagnosis_energy_use_settings_search_by_diagnosis_id__diagnosis_id__get(
            diagnosisId!
          ),
        {
          enabled: !!diagnosisId,
          onSuccess: data => {
            if (data) {
              setSettingsFormData({
                default_energy_relevance_lower_limit: data.default_energy_relevance_lower_limit,
                default_energy_relevance_upper_limit: data.default_energy_relevance_upper_limit,
                weight_energy_relevance: data.weight_energy_relevance,
                weight_technological_gap_vs_bats: data.weight_technological_gap_vs_bats,
                weight_plant_management: data.weight_plant_management,
                knowledge_measurement: data.knowledge_measurement,
                minimum_threshold: data.minimum_threshold,
              });
            }
          },
        }
      );

    type ImportEnergyUsePayload =
      | { source: 'diagnosis'; row: DiagnosisEnergyUseReadDTO }
      | { source: 'catalog'; row: EnergyUseReadDTO };

    const importEnergyUseToCurrentMutation = useMutation(
      async (payload: ImportEnergyUsePayload) => {
        if (!diagnosisId) throw new Error('Manca diagnosisId');
        if (payload.source === 'catalog') {
          const { row } = payload;
          if (!row.id) throw new Error('Voce catalogo senza id.');
          const codeNorm = (row.code || '').trim();
          if (codeNorm) {
            const dupCode = energyUse.some(u => (u.code || '').trim() === codeNorm);
            if (dupCode) {
              throw new Error(`Esiste già un uso energetico con codice "${codeNorm}".`);
            }
          }
          if (energyUse.some(u => u.original_energy_use_id === row.id)) {
            throw new Error('Questo uso da catalogo è già presente nella diagnosi attuale.');
          }
          return apiClient.copy_energy_use_diagnosis_energy_use_copy_post({
            energy_use_id: row.id,
            diagnosis_id: diagnosisId,
          } as any);
        }
        const { row } = payload;
        const codeNorm = (row.code || '').trim();
        if (codeNorm) {
          const dupCode = energyUse.some(u => (u.code || '').trim() === codeNorm);
          if (dupCode) {
            throw new Error(`Esiste già un uso energetico con codice "${codeNorm}".`);
          }
        }
        if (row.original_energy_use_id) {
          const dupOrig = energyUse.some(
            u => u.original_energy_use_id === row.original_energy_use_id
          );
          if (dupOrig) {
            throw new Error('Questo uso da catalogo è già presente nella diagnosi attuale.');
          }
          return apiClient.copy_energy_use_diagnosis_energy_use_copy_post({
            energy_use_id: row.original_energy_use_id,
            diagnosis_id: diagnosisId,
          } as any);
        }
        return apiClient.create_item_diagnosis_energy_use__post({
          diagnosis_id: diagnosisId,
          code: row.code || '',
          description: row.description,
        } as any);
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(`diagnosis-energy-use-${diagnosisId}`);
          queryClient.invalidateQueries(`diagnosis-energy-use-vector-data-${diagnosisId}`);
          queryClient.invalidateQueries(`diagnosis-energy-use-totals-data-${diagnosisId}`);
          toast.success('Uso energetico aggiunto agli attuali');
        },
        onError: (e: any) => {
          toast.error(e?.message || 'Impossibile aggiungere l’uso energetico');
        },
      }
    );

    const deleteMutation = useMutation(
      async (id: string) => {
        await apiClient.delete_item_diagnosis_energy_use__item_id__delete(id);
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(`diagnosis-energy-use-${diagnosisId}`);
          toast.success('Uso energetico eliminato con successo');
          setDeleteDialogState({ open: false, id: null });
        },
        onError: (error: any) => {
          toast.error(error?.message || "Eliminazione dell'uso energetico non riuscita");
        },
      }
    );

    // Stato per i valori modificabili dall'utente (deprecato, usato solo per compatibilità)
    const [editedValues, setEditedValues] = useState<
      Record<
        string,
        {
          technological_gap_vs_bats?: number | null;
          plant_management?: number | null;
          knowledge_measurement?: number | null;
        }
      >
    >({});

    // Stato locale per le righe della Tab 0 (editing inline)
    const [rows, setRows] = useState<any[]>([]);

    // Ref per tracciare le modifiche locali
    const editedMapRef = useRef<
      Map<
        string,
        Partial<{
          technological_gap_vs_bats?: number | null;
          plant_management?: number | null;
          knowledge_measurement?: number | null;
        }>
      >
    >(new Map());

    // Mutation per ricalcolare i dati
    const recalculateMutation = useMutation(
      async () => {
        await apiClient.recalculate_diagnosis_energy_use_vector_data_recalculate_post({
          diagnosis_id: diagnosisId!,
        } as any);
        await apiClient.recalculate_diagnosis_energy_use_totals_data_recalculate_post({
          diagnosis_id: diagnosisId!,
        } as any);
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(`diagnosis-energy-use-vector-data-${diagnosisId}`);
          queryClient.invalidateQueries(`diagnosis-energy-use-totals-data-${diagnosisId}`);
          toast.success('Dati ricalcolati con successo');
        },
        onError: (error: any) => {
          toast.error('Errore durante il ricalcolo dei dati');
        },
      }
    );

    // Mutation per aggiornare i valori utente
    const updateUserValuesMutation = useMutation(
      async ({ itemId, values }: { itemId: string; values: any }) => {
        return await apiClient.update_user_values_diagnosis_energy_use_totals_data__item_id__update_user_values_put(
          itemId,
          values as any
        );
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(`diagnosis-energy-use-totals-data-${diagnosisId}`);
          setEditedValues({});
        },
        onError: (error: any) => {
          toast.error("Errore durante l'aggiornamento dei valori");
        },
      }
    );

    const updateSettingsMutation = useMutation(
      async (data: DiagnosisEnergyUseSettingsCreateDTO) => {
        if (!settings?.id) {
          throw new Error('Settings not found');
        }
        return await apiClient.update_item_diagnosis_energy_use_settings__item_id__put(
          settings.id,
          data
        );
      },
      {
        onSuccess: async () => {
          queryClient.invalidateQueries(`diagnosis-energy-use-settings-${diagnosisId}`);
          queryClient.invalidateQueries(`diagnosis-energy-use-totals-data-${diagnosisId}`);
          toast.success('Impostazioni aggiornate con successo');
        },
        onError: (error: any) => {
          if (Array.isArray(error?.detail)) {
            for (const detail of error.detail) {
              toast.error(`${detail.msg} (${detail.loc[1]})`);
            }
          } else {
            toast.error('Aggiornamento delle impostazioni non riuscito');
          }
        },
      }
    );

    React.useImperativeHandle(ref, () => ({
      save: async () => {
        // Salva i valori modificati dall'utente nella prima tab usando editedMapRef
        if (editedMapRef.current.size > 0) {
          try {
            await handleSaveEdited();
            return { success: true };
          } catch (error) {
            return { success: false, error };
          }
        }
        toast.success('Nessuna modifica da salvare');
        return { success: true };
      },
      add: async () => {
        if (!diagnosisId) {
          toast.error('Salva prima i dati principali della diagnosi.');
          return;
        }
        setSelectedEnergyUse(null);
        setEditDialogOpen(true);
      },
    }));

    const handleEditClick = (id: GridRowId) => {
      const item = energyUse.find(eu => eu.id === id);
      if (item) {
        setSelectedEnergyUse(item);
        setEditDialogOpen(true);
      }
    };

    const handleDeleteClick = (id: GridRowId) => {
      setDeleteDialogState({ open: true, id: id as string });
    };

    const handleCloseDeleteDialog = () => {
      setDeleteDialogState({ open: false, id: null });
    };

    const handleConfirmDelete = () => {
      if (deleteDialogState.id) {
        deleteMutation.mutate(deleteDialogState.id);
      }
    };

    const handleImportEnergyUseToCurrent = useCallback(
      (row: DiagnosisEnergyUseReadDTO) => {
        if (!diagnosisId) return;
        importEnergyUseToCurrentMutation.mutate({ source: 'diagnosis', row });
      },
      [importEnergyUseToCurrentMutation, diagnosisId]
    );

    const handleImportCatalogEnergyUseToCurrent = useCallback(
      (row: EnergyUseReadDTO) => {
        if (!diagnosisId) return;
        importEnergyUseToCurrentMutation.mutate({ source: 'catalog', row });
      },
      [importEnergyUseToCurrentMutation, diagnosisId]
    );

    const handleImportAiEnergyUse = useCallback(
      async (item: AiSearchResultItem, row: DiagnosisEnergyUseReadDTO) => {
        if (!diagnosisId) return;
        try {
          await importAiItemToDiagnosis({
            diagnosisId,
            item,
            apiClient,
            queryClient,
            existingCodes: energyUse.map(u => (u.code || '').trim()).filter(Boolean) as string[],
          });
          queryClient.invalidateQueries(`diagnosis-energy-use-${diagnosisId}`);
          queryClient.invalidateQueries(`diagnosis-energy-use-vector-data-${diagnosisId}`);
          queryClient.invalidateQueries(`diagnosis-energy-use-totals-data-${diagnosisId}`);
          toast.success('Uso energetico aggiunto agli attuali');
        } catch (e: unknown) {
          toast.error(e instanceof Error ? e.message : 'Impossibile aggiungere l’uso energetico');
        }
      },
      [diagnosisId, energyUse, queryClient]
    );

    const handleCloseEditDialog = () => {
      setEditDialogOpen(false);
      setSelectedEnergyUse(null);
    };

    const handleSaveSettings = () => {
      if (settingsFormData) {
        updateSettingsMutation.mutate(settingsFormData as any);
      }
    };

    const handleRefresh = () => {
      recalculateMutation.mutate();
    };

    const handleViewModeChange = (_: React.MouseEvent<HTMLElement>, nextMode: ViewMode | null) => {
      if (nextMode) {
        setViewMode(nextMode);
      }
    };

    // Prepara i dati per la griglia della prima tab
    const gridData = useMemo(() => {
      if (!energyUse.length || !vectorData.length || !totalsData.length) {
        return [];
      }

      return energyUse.map(use => {
        const totalData = totalsData.find(t => t.energy_use_id === use.id);
        const row: any = {
          id: use.id,
          energy_use_code: use.code,
          energy_use_description: use.description,
        };

        // Aggiungi colonne per ogni vettore
        vectors.forEach(vector => {
          const vectorDataItem = vectorData.find(
            v => v.energy_use_id === use.id && v.energy_vector_id === vector.id
          );
          row[`vector_${vector.id}_consumption`] = vectorDataItem?.consumption ?? null;
          row[`vector_${vector.id}_percentage`] = vectorDataItem?.percentage ?? null;
          row[`vector_${vector.id}_cost`] = vectorDataItem?.cost ?? null;
        });

        // Aggiungi colonne totali
        if (totalData) {
          row.total_consumption = totalData.total_consumption;
          row.total_percentage = totalData.total_percentage;
          row.energy_relevance = totalData.energy_relevance;
          row.technological_gap_vs_bats = totalData.technological_gap_vs_bats;
          row.plant_management = totalData.plant_management;
          row.knowledge_measurement = totalData.knowledge_measurement;
          row.weighted_average_criticality = totalData.weighted_average_criticality;
          row.significance_of_energy_use = totalData.significance_of_energy_use;
          row.totals_data_id = totalData.id ?? null;
        } else {
          // Inizializza i campi anche se totalData non esiste
          row.total_consumption = null;
          row.total_percentage = null;
          row.energy_relevance = null;
          row.technological_gap_vs_bats = null;
          row.plant_management = null;
          row.knowledge_measurement = null;
          row.weighted_average_criticality = null;
          row.significance_of_energy_use = null;
          row.totals_data_id = null;
        }

        return row;
      });
    }, [energyUse, vectorData, totalsData, vectors]);

    // Calcola la riga dei totali
    const totalRow = useMemo(() => {
      if (gridData.length === 0) {
        return null;
      }

      const total: any = {
        energy_use_code: 'TOTALE',
        energy_use_description: '',
      };

      // Calcola totali per ogni vettore
      vectors.forEach(vector => {
        const consumptionTotal = gridData.reduce((sum, row) => {
          const val = row[`vector_${vector.id}_consumption`];
          return sum + (val != null ? Number(val) : 0);
        }, 0);
        total[`vector_${vector.id}_consumption`] = consumptionTotal;

        const percentageTotal = gridData.reduce((sum, row) => {
          const val = row[`vector_${vector.id}_percentage`];
          return sum + (val != null ? Number(val) : 0);
        }, 0);
        total[`vector_${vector.id}_percentage`] = percentageTotal;

        const costTotal = gridData.reduce((sum, row) => {
          const val = row[`vector_${vector.id}_cost`];
          return sum + (val != null ? Number(val) : 0);
        }, 0);
        total[`vector_${vector.id}_cost`] = costTotal;
      });

      // Calcola totali generali
      const totalConsumption = gridData.reduce((sum, row) => {
        const val = row.total_consumption;
        return sum + (val != null ? Number(val) : 0);
      }, 0);
      total.total_consumption = totalConsumption;

      const totalPercentage = gridData.reduce((sum, row) => {
        const val = row.total_percentage;
        return sum + (val != null ? Number(val) : 0);
      }, 0);
      total.total_percentage = totalPercentage;

      // I campi editabili e altri campi non hanno senso per la riga totale
      total.energy_relevance = null;
      total.technological_gap_vs_bats = null;
      total.plant_management = null;
      total.knowledge_measurement = null;
      total.weighted_average_criticality = null;
      total.significance_of_energy_use = null;
      total.totals_data_id = null;

      return total;
    }, [gridData, vectors]);

    // Sincronizza rows con gridData quando cambia
    useEffect(() => {
      if (gridData.length > 0) {
        setRows(gridData);
      }
    }, [gridData]);

    // Funzione per merge delle righe preservando identità
    const mergeRows = useCallback((prevRows: any[], nextRows: any[]) => {
      const prevMap = new Map(prevRows.map(r => [r.id, r]));
      return nextRows.map(nextRow => {
        const prevRow = prevMap.get(nextRow.id);
        if (prevRow && prevRow.id === nextRow.id) {
          // Preserva l'oggetto precedente se l'ID corrisponde
          return prevRow;
        }
        return nextRow;
      });
    }, []);

    // Funzione processRowUpdate per editing inline
    const processRowUpdate = useCallback(async (newRow: any, oldRow: any) => {
      const itemId = newRow.totals_data_id;
      if (!itemId) {
        return oldRow;
      }

      // Identifica i campi editabili
      const editableFields = [
        'technological_gap_vs_bats',
        'plant_management',
        'knowledge_measurement',
      ];
      const changes: Partial<{
        technological_gap_vs_bats?: number | null;
        plant_management?: number | null;
        knowledge_measurement?: number | null;
      }> = {};

      // Trova solo i campi effettivamente cambiati
      editableFields.forEach(field => {
        const oldValue = oldRow[field];
        const newValue = newRow[field];
        if (oldValue !== newValue) {
          changes[field as keyof typeof changes] = newValue;
        }
      });

      // Se ci sono cambiamenti, salvali in editedMapRef
      if (Object.keys(changes).length > 0) {
        const existing = editedMapRef.current.get(itemId) || {};
        editedMapRef.current.set(itemId, { ...existing, ...changes });
      }

      // Aggiorna lo stato rows
      setRows(prevRows => {
        return prevRows.map(row => {
          if (row.id === newRow.id) {
            return { ...row, ...newRow };
          }
          return row;
        });
      });

      return newRow;
    }, []);

    // Funzione per salvare le modifiche
    const handleSaveEdited = useCallback(async () => {
      if (editedMapRef.current.size === 0) {
        toast.success('Nessuna modifica da salvare');
        return;
      }

      try {
        const updates = Array.from(editedMapRef.current.entries()).map(([itemId, values]) => ({
          itemId,
          values,
        }));

        await Promise.all(
          updates.map(update =>
            updateUserValuesMutation.mutateAsync({
              itemId: update.itemId,
              values: update.values,
            })
          )
        );

        editedMapRef.current.clear();
        toast.success('Valori aggiornati con successo');
      } catch (error) {
        toast.error("Errore durante l'aggiornamento dei valori");
      }
    }, [updateUserValuesMutation]);

    // Funzione per resettare le modifiche locali
    const handleResetLocal = useCallback(() => {
      editedMapRef.current.clear();
      setRows(gridData);
      toast.success('Modifiche locali annullate');
    }, [gridData]);

    // Crea le colonne per la griglia della Tab 0 (con editing inline)
    const gridFields = useMemo<ReadOnlyField[]>(() => {
      const fields: ReadOnlyField[] = [
        {
          field: 'energy_use_code',
          headerName: 'Uso energetico',
          width: 200,
          sticky: 'left',
        },
      ];

      // Colonne per ogni vettore: intestazione = label del tipo di vettore energetico
      vectors.forEach(vector => {
        const vectorName =
          vectorTypeOptions.find(opt => opt.value === vector.vector_type)?.label ||
          vector.vector_type ||
          vector.code ||
          'Vettore';
        const unitOfMeasurement = vector.unit_of_measurement || '';

        fields.push({
          field: `vector_${vector.id}_consumption`,
          headerName: `${vectorName}${unitOfMeasurement ? ` [${formatUnitOfMeasurement(unitOfMeasurement)}]` : ''}`,
          width: 150,
          type: 'number',
          decimalPlaces: DECIMAL_PLACES.usi_energetici_quantita,
        });
        fields.push({
          field: `vector_${vector.id}_percentage`,
          headerName: `${vectorName} (%)`,
          width: 120,
          type: 'number',
          valueFormatter: (value: any) => {
            if (value == null) return '';
            return `${formatNumberItalian(value, { maximumFractionDigits: DECIMAL_PLACES.usi_energetici_percentuali, minimumFractionDigits: 0 })}%`;
          },
        });
        fields.push({
          field: `vector_${vector.id}_cost`,
          headerName: `${vectorName} (€)`,
          width: 150,
          type: 'number',
          valueFormatter: (value: any) => {
            if (value == null) return '';
            return `€ ${formatNumberItalian(value, { maximumFractionDigits: DECIMAL_PLACES.usi_energetici_valori_eur, minimumFractionDigits: 0 })}`;
          },
        });
      });

      // Colonne totali
      if (settings) {
        fields.push(
          {
            field: 'total_consumption',
            headerName: 'Consumo annuo (Tep)',
            width: 180,
            type: 'number',
            decimalPlaces: DECIMAL_PLACES.usi_energetici_tep,
          },
          {
            field: 'total_percentage',
            headerName: 'Consumo annuo (%)',
            width: 150,
            type: 'number',
            valueFormatter: (value: any) => {
              if (value == null) return '';
              return `${formatNumberItalian(value, { maximumFractionDigits: DECIMAL_PLACES.usi_energetici_percentuali, minimumFractionDigits: 0 })}%`;
            },
          },
          {
            field: 'energy_relevance',
            headerName: `Rilevanza energetica (${settings.weight_energy_relevance}%)`,
            width: 220,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => {
              const value = params.value;
              if (value == null) return '';
              const color = value === 1 ? 'success' : value === 2 ? 'warning' : 'error';
              return (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: '100%',
                  }}
                >
                  <Chip label={value} color={color} size="small" />
                </Box>
              );
            },
          },
          {
            field: 'technological_gap_vs_bats',
            headerName: `Gap tecnologico vs BATs (${settings.weight_technological_gap_vs_bats}%)`,
            width: 250,
            align: 'center',
            headerAlign: 'center',
            type: 'singleSelect',
            valueOptions: [1, 2, 3],
            valueFormatter: (value: any) => {
              if (value == null || value === '') return '-';
              return String(value);
            },
            renderCell: (params: any) => {
              if (params.row?._isTotalRow) return null;
              const value = params.value;
              if (value == null || value === '') {
                return (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: '100%',
                      height: '100%',
                    }}
                  >
                    <Typography variant="body2">-</Typography>
                  </Box>
                );
              }
              const color = value === 1 ? 'success' : value === 2 ? 'warning' : 'error';
              return (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: '100%',
                  }}
                >
                  <Chip label={value} color={color} size="small" />
                </Box>
              );
            },
          },
          {
            field: 'plant_management',
            headerName: `Gestione impianti (${settings.weight_plant_management}%)`,
            width: 220,
            align: 'center',
            headerAlign: 'center',
            type: 'singleSelect',
            valueOptions: [1, 2, 3],
            valueFormatter: (value: any) => {
              if (value == null || value === '') return '-';
              return String(value);
            },
            renderCell: (params: any) => {
              if (params.row?._isTotalRow) return null;
              const value = params.value;
              if (value == null || value === '') {
                return (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: '100%',
                      height: '100%',
                    }}
                  >
                    <Typography variant="body2">-</Typography>
                  </Box>
                );
              }
              const color = value === 1 ? 'success' : value === 2 ? 'warning' : 'error';
              return (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: '100%',
                  }}
                >
                  <Chip label={value} color={color} size="small" />
                </Box>
              );
            },
          },
          {
            field: 'knowledge_measurement',
            headerName: `Conoscenza / Misurazione (${settings.knowledge_measurement}%)`,
            width: 280,
            align: 'center',
            headerAlign: 'center',
            type: 'singleSelect',
            valueOptions: [1, 2, 3],
            valueFormatter: (value: any) => {
              if (value == null || value === '') return '-';
              return String(value);
            },
            renderCell: (params: any) => {
              if (params.row?._isTotalRow) return null;
              const value = params.value;
              if (value == null || value === '') {
                return (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: '100%',
                      height: '100%',
                    }}
                  >
                    <Typography variant="body2">-</Typography>
                  </Box>
                );
              }
              const color = value === 1 ? 'success' : value === 2 ? 'warning' : 'error';
              return (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: '100%',
                  }}
                >
                  <Chip label={value} color={color} size="small" />
                </Box>
              );
            },
          },
          {
            field: 'weighted_average_criticality',
            headerName: 'Media pesata valutazioni',
            width: 200,
            type: 'number',
            decimalPlaces: DECIMAL_PLACES.usi_energetici_media_pesata,
          },
          {
            field: 'significance_of_energy_use',
            headerName: 'Significatività uso energetico',
            width: 220,
            renderCell: (params: any) => {
              if (params.row?._isTotalRow) return null;
              const value = params.value;
              if (value) {
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                    <Typography color="primary">Significativo</Typography>
                  </Box>
                );
              }
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                  Non significativo
                </Box>
              );
            },
          }
        );
      }

      return fields;
    }, [vectors, settings, vectorTypeOptions]);

    // Calcola i dati per i grafici a torta
    const chartsData = useMemo(() => {
      if (!gridData.length || !vectors.length || !energyUse.length) {
        return {
          totalChartData: [],
          vectorChartsData: new Map<string, ChartDatum[]>(),
        };
      }

      // Grafico totale: distribuzione per uso energetico usando total_percentage da DiagnosisEnergyUseTotalsData
      const totalChartData: ChartDatum[] = gridData
        .filter(row => row.total_consumption != null && row.total_percentage != null)
        .map((row, index) => {
          const energyUseItem = energyUse.find(eu => eu.id === row.id);
          const label = energyUseItem
            ? `${energyUseItem.code || energyUseItem.description || row.id}`
            : row.energy_use_code || row.id;
          return {
            id: row.id,
            label,
            value: Number(row.total_consumption) || 0,
            percentage: Number(row.total_percentage) || 0,
            color: energyUseColors[index % energyUseColors.length],
          };
        })
        .filter(datum => datum.value > 0);

      // Grafici per vettore: distribuzione % per uso energetico usando percentage da DiagnosisEnergyUseVectorData
      const vectorChartsData = new Map<string, ChartDatum[]>();
      vectors.forEach((vector, vectorIndex) => {
        if (!vector.id) return;
        const vectorData: ChartDatum[] = [];

        // Crea i dati per ogni uso energetico usando le percentuali già calcolate
        gridData.forEach((row, rowIndex) => {
          const consumption = row[`vector_${vector.id}_consumption`];
          const percentage = row[`vector_${vector.id}_percentage`];
          if (consumption != null && percentage != null) {
            const value = Number(consumption);
            // Filtra solo i valori maggiori di zero
            if (value > 0) {
              const energyUseItem = energyUse.find(eu => eu.id === row.id);
              const label = energyUseItem
                ? `${energyUseItem.code || energyUseItem.description || row.id}`
                : row.energy_use_code || row.id;
              vectorData.push({
                id: `${vector.id}-${row.id}`,
                label,
                value,
                percentage: Number(percentage),
                color: energyUseColors[rowIndex % energyUseColors.length],
              });
            }
          }
        });

        if (vectorData.length > 0) {
          vectorChartsData.set(vector.id, vectorData);
        }
      });

      return {
        totalChartData,
        vectorChartsData,
      };
    }, [gridData, vectors, energyUse]);

    const currentEnergyUseFields = useMemo<ReadOnlyField[]>(
      () => [{ field: 'code', headerName: 'Codice', width: 280, sticky: 'left' }],
      []
    );

    const energyUseImportField = useMemo<ReadOnlyField>(
      () => ({
        field: '_import',
        headerName: '',
        width: 44,
        sticky: 'left',
        renderCell: (params: any) => {
          const row = params?.row as DiagnosisEnergyUseReadDTO;
          if (!row?.id) return null;
          return (
            <Tooltip title="Aggiungi agli usi attuali">
              <IconButton
                size="small"
                disabled={importEnergyUseToCurrentMutation.isLoading}
                onClick={() => handleImportEnergyUseToCurrent(row)}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          );
        },
      }),
      [importEnergyUseToCurrentMutation.isLoading, handleImportEnergyUseToCurrent]
    );

    const catalogEnergyUseImportField = useMemo<ReadOnlyField>(
      () => ({
        field: '_import',
        headerName: '',
        width: 44,
        sticky: 'left',
        renderCell: (params: any) => {
          const row = params?.row as EnergyUseReadDTO;
          if (!row?.id) return null;
          if (isCatalogEnergyUseAlreadyPresent(row)) {
            return (
              <Tooltip title="Voce già presente negli usi attuali">
                <span>
                  <IconButton size="small" disabled>
                    <ArrowBackIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            );
          }
          return (
            <Tooltip title="Aggiungi agli usi attuali">
              <IconButton
                size="small"
                disabled={importEnergyUseToCurrentMutation.isLoading}
                onClick={() => handleImportCatalogEnergyUseToCurrent(row)}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          );
        },
      }),
      [
        importEnergyUseToCurrentMutation.isLoading,
        handleImportCatalogEnergyUseToCurrent,
        isCatalogEnergyUseAlreadyPresent,
      ]
    );

    /** Badge "Già presente" per le voci di catalogo già importate nella diagnosi attuale. */
    const catalogEnergyUseStatusField = useMemo<ReadOnlyField>(
      () => ({
        field: '_status',
        headerName: '',
        width: 130,
        renderCell: (params: any) => {
          const row = params?.row as EnergyUseReadDTO;
          if (!isCatalogEnergyUseAlreadyPresent(row)) return null;
          return <Chip label="Già presente" size="small" variant="outlined" />;
        },
      }),
      [isCatalogEnergyUseAlreadyPresent]
    );

    /** Stesse colonne della colonna «Diagnosi precedente»: ← + Codice. */
    const sideEnergyUseFields = useMemo<ReadOnlyField[]>(
      () => [
        energyUseImportField,
        { field: 'code', headerName: 'Codice', width: 220, sticky: 'left' },
      ],
      [energyUseImportField]
    );

    const catalogSideEnergyUseFields = useMemo<ReadOnlyField[]>(
      () => [
        catalogEnergyUseImportField,
        { field: 'code', headerName: 'Codice', width: 220, sticky: 'left' },
        catalogEnergyUseStatusField,
      ],
      [catalogEnergyUseImportField, catalogEnergyUseStatusField]
    );

    const aiEnergyUseImportField = useMemo<ReadOnlyField>(
      () => ({
        field: '_import',
        headerName: '',
        width: 44,
        sticky: 'left',
        renderCell: (params: any) => {
          const row = params?.row as DiagnosisEnergyUseReadDTO;
          const item = row?.id ? aiEnergyUseItemByRowId.get(row.id) : undefined;
          if (!item) return null;
          return (
            <Tooltip title="Aggiungi agli usi attuali">
              <IconButton
                size="small"
                disabled={importEnergyUseToCurrentMutation.isLoading}
                onClick={() => void handleImportAiEnergyUse(item, row)}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          );
        },
      }),
      [aiEnergyUseItemByRowId, handleImportAiEnergyUse, importEnergyUseToCurrentMutation.isLoading]
    );

    const aiSideEnergyUseFields = useMemo<ReadOnlyField[]>(
      () => [
        aiEnergyUseImportField,
        { field: 'code', headerName: 'Codice', width: 220, sticky: 'left' },
        aiFonteReadOnlyField,
      ],
      [aiEnergyUseImportField]
    );

    const actions: RowAction[] = [
      {
        label: 'Modifica',
        icon: <EditIcon />,
        onClick: handleEditClick,
        color: 'inherit',
      },
      {
        label: 'Elimina',
        icon: <DeleteIcon />,
        onClick: handleDeleteClick,
        color: 'inherit',
      },
    ];

    if (!diagnosisId) {
      return (
        <Typography color="text.secondary">
          Salva la diagnosi per gestire gli usi energetici.
        </Typography>
      );
    }

    return (
      <Stack spacing={2} sx={{ p: 2 }}>
        <Stack
          direction="row"
          spacing="1rem"
          alignItems="center"
          flexWrap="wrap"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing="1rem" alignItems="center" flexWrap="wrap">
            <Typography variant="h6">Usi energetici</Typography>
            <Tabs
              value={tabValue}
              onChange={(_, newValue) => setTabValue(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ minHeight: 40 }}
            >
              <Tab label="Usi energetici" sx={{ textTransform: 'none' }} />
              <Tab label="Analisi usi energetici" sx={{ textTransform: 'none' }} />
              <Tab label="Impostazioni" sx={{ textTransform: 'none' }} />
            </Tabs>
          </Stack>
          {tabValue === 0 && (
            <Stack direction="row" spacing={2} alignItems="center">
              <IconButton
                color="primary"
                aria-label="Ricerca AI usi energetici"
                onClick={() => setAiSearchOpen(true)}
                size="medium"
              >
                <SearchIcon />
              </IconButton>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                disabled={recalculateMutation.isLoading}
              >
                Aggiorna
              </Button>
            </Stack>
          )}
          {tabValue === 1 && (
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              size="small"
              color="primary"
            >
              <ToggleButton value="table" sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TableRowsIcon fontSize="small" />
                <Typography variant="body2">Tabella</Typography>
              </ToggleButton>
              <ToggleButton value="charts" sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <ShowChartIcon fontSize="small" />
                <Typography variant="body2">Grafici</Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </Stack>

        <TabPanel value={tabValue} index={0}>
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={1}
            sx={{ alignItems: 'stretch', width: '100%' }}
          >
            <Paper
              variant="outlined"
              sx={{
                flex: 1,
                minWidth: 0,
                p: 1.5,
                borderRadius: 3,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Attuali
              </Typography>
              <ReadOnlyDataGrid
                fields={currentEnergyUseFields}
                data={energyUse}
                actions={actions}
                height="calc(100vh - 420px)"
                getId={row => row.id ?? ''}
                loading={isLoading}
              />
            </Paper>
            <Paper
              variant="outlined"
              sx={{
                flex: 1,
                minWidth: 0,
                p: 1.5,
                borderRadius: 3,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Diagnosi precedente
              </Typography>
              {!previousDiagnosisId && !isLoadingPreviousDiagnosis ? (
                <Typography variant="body2" color="text.secondary">
                  Nessuna diagnosi precedente sullo stesso sito base.
                </Typography>
              ) : (
                <ReadOnlyDataGrid
                  fields={sideEnergyUseFields}
                  data={previousEnergyUsesFiltered}
                  actions={[]}
                  height="calc(100vh - 420px)"
                  getId={row => row.id ?? ''}
                  loading={isLoadingPreviousDiagnosis || isLoadingPreviousUses}
                />
              )}
            </Paper>
            <Paper
              variant="outlined"
              sx={{
                flex: 1,
                minWidth: 0,
                p: 1.5,
                borderRadius: 3,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Catalogo standard
              </Typography>
              <ReadOnlyDataGrid
                fields={catalogSideEnergyUseFields}
                data={catalogEnergyUses}
                actions={[]}
                height="calc(100vh - 420px)"
                getId={row => row.id ?? ''}
                loading={isLoadingCatalogEnergyUses}
              />
            </Paper>
            <Paper
              variant="outlined"
              sx={{
                flex: 1,
                minWidth: 0,
                p: 1.5,
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Suggeriti AI
              </Typography>
              {aiSuggestedEnergyUses.length === 0 && !isLoadingAiEnergyUses ? (
                <Typography variant="body2" color="text.secondary">
                  Nessun suggerimento dall&apos;AI per questa diagnosi.
                </Typography>
              ) : (
                <ReadOnlyDataGrid
                  fields={aiSideEnergyUseFields}
                  data={aiSuggestedEnergyUses}
                  actions={[]}
                  height="calc(100vh - 420px)"
                  getId={row => row.id ?? String(row.code)}
                  loading={isLoadingAiEnergyUses}
                />
              )}
            </Paper>
          </Stack>
          <ConfirmDialog
            open={deleteDialogState.open}
            onCancel={handleCloseDeleteDialog}
            onConfirm={handleConfirmDelete}
            title="Elimina uso energetico"
            content="Sei sicuro di voler eliminare questo uso energetico?"
          />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: viewMode === 'table' ? 'block' : 'none' }}>
            <ReadOnlyDataGrid
              columnBufferPx={20000}
              fields={gridFields}
              data={rows}
              loading={isLoadingVectorData || isLoadingTotalsData || isLoadingVectors}
              height="calc(100vh - 380px)"
              getRowId={row => row.id ?? ''}
              getId={row => row.id ?? ''}
              showFooter={false}
              editable={true}
              processRowUpdate={processRowUpdate}
              onProcessRowUpdateError={error => {
                toast.error(`Errore durante l'aggiornamento: ${error.message}`);
              }}
              totalRow={totalRow}
              totalRowId="_total"
              customSx={{
                '& .MuiDataGrid-cell[data-field="energy_use_code"]': {
                  borderRight: '1px solid rgba(224, 224, 224, 1)',
                },
                '& .MuiDataGrid-columnHeader[data-field="energy_use_code"]': {
                  borderRight: '1px solid rgba(224, 224, 224, 1)',
                },
                // Bordo dopo ogni colonna cost di ogni vettore
                ...vectors.reduce(
                  (acc, vector) => {
                    acc[`& .MuiDataGrid-cell[data-field="vector_${vector.id}_cost"]`] = {
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                    };
                    acc[`& .MuiDataGrid-columnHeader[data-field="vector_${vector.id}_cost"]`] = {
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                    };
                    return acc;
                  },
                  {} as Record<string, any>
                ),
                // Stile per la riga totale (gestito da ReadOnlyDataGrid)
                '& .total-row': {
                  backgroundColor: 'background.paper',
                  color: 'text.primary',
                  fontWeight: 'bold',
                  // Hover theme-aware (grey[200] risultava bianco/illeggibile in dark mode)
                  '&:hover, &:hover .MuiDataGrid-cell': {
                    backgroundColor: (theme: any) => theme.palette.action.hover,
                    color: 'text.primary',
                  },
                },
              }}
            />
          </Box>

          <Box sx={{ display: viewMode === 'charts' ? 'block' : 'none' }}>
            {chartsData.totalChartData.length === 0 ? (
              <Typography color="text.secondary">Nessun dato disponibile per i grafici.</Typography>
            ) : (
              <Box
                sx={{
                  border: theme => `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  p: 2,
                }}
              >
                <Stack spacing={2}>
                  {/* Grafico totale consumo annuo */}
                  {chartsData.totalChartData.length > 0 && (
                    <Box>
                      <Accordion defaultExpanded disableGutters elevation={0}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="subtitle1" fontWeight={600}>
                            Consumo annuo totale
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box sx={{ display: 'flex', justifyContent: 'center', height: 400 }}>
                            <PieChartWrapper
                              width={1000}
                              height={400}
                              unitOfMeasurement="tep"
                              slotProps={{
                                legend: {
                                  direction: 'column',
                                  position: { vertical: 'middle', horizontal: 'right' },
                                },
                              }}
                              margin={{ top: 20, right: 320, left: 20, bottom: 20 }}
                              series={[
                                {
                                  data: chartsData.totalChartData,
                                  arcLabel: item => {
                                    return '';
                                    const datum = item as any;
                                    return `${datum.label} (${datum.percentage.toFixed(1)}%)`;
                                  },
                                  valueFormatter: ({ value }) => {
                                    return `${formatNumberItalian(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                                  },
                                  highlightScope: { fade: 'global', highlight: 'item' },
                                  highlighted: { additionalRadius: 2 },
                                  cornerRadius: 3,
                                },
                              ]}
                              sx={{
                                [`& .${pieArcLabelClasses.root}`]: {
                                  fontSize: '12px',
                                },
                              }}
                            />
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    </Box>
                  )}

                  {/* Grafici per vettore energetico */}
                  {vectors
                    .filter(vector => vector.id)
                    .map(vector => {
                      const vectorChartData = chartsData.vectorChartsData.get(vector.id!);
                      if (!vectorChartData || vectorChartData.length === 0) {
                        return null;
                      }

                      return (
                        <Box key={vector.id}>
                          <Accordion disableGutters elevation={0}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography variant="subtitle1" fontWeight={600}>
                                {vectorTypeOptions.find(opt => opt.value === vector.vector_type)
                                  ?.label ||
                                  vector.code ||
                                  vector.id}{' '}
                                - Distribuzione per uso energetico
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Box sx={{ display: 'flex', justifyContent: 'center', height: 400 }}>
                                <PieChartWrapper
                                  width={1000}
                                  height={400}
                                  unitOfMeasurement={vector.unit_of_measurement || ''}
                                  slotProps={{
                                    legend: {
                                      direction: 'column',
                                      position: { vertical: 'middle', horizontal: 'right' },
                                    },
                                  }}
                                  margin={{ top: 20, right: 320, left: 20, bottom: 20 }}
                                  series={[
                                    {
                                      data: vectorChartData,
                                      arcLabel: item => {
                                        return '';
                                        const datum = item as any;
                                        return `${datum.label} (${datum.percentage.toFixed(1)}%)`;
                                      },
                                      valueFormatter: ({ value }) => {
                                        return `${formatNumberItalian(value, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
                                      },
                                      highlightScope: { fade: 'global', highlight: 'item' },
                                      highlighted: { additionalRadius: 2 },
                                      cornerRadius: 3,
                                    },
                                  ]}
                                  sx={{
                                    [`& .${pieArcLabelClasses.root}`]: {
                                      fontSize: '12px',
                                    },
                                  }}
                                />
                              </Box>
                            </AccordionDetails>
                          </Accordion>
                        </Box>
                      );
                    })}
                </Stack>
              </Box>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {isLoadingSettings ? (
            <Typography>Caricamento...</Typography>
          ) : settingsFormData ? (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Rilevanza Energetica Limite Inferiore"
                  type="number"
                  value={settingsFormData.default_energy_relevance_lower_limit ?? ''}
                  onChange={e =>
                    setSettingsFormData({
                      ...settingsFormData,
                      default_energy_relevance_lower_limit: parseInt(e.target.value) || undefined,
                    } as any)
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Rilevanza Energetica Limite Superiore"
                  type="number"
                  value={settingsFormData.default_energy_relevance_upper_limit ?? ''}
                  onChange={e =>
                    setSettingsFormData({
                      ...settingsFormData,
                      default_energy_relevance_upper_limit: parseInt(e.target.value) || undefined,
                    } as any)
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Peso Rilevanza Energetica"
                  type="number"
                  value={settingsFormData.weight_energy_relevance ?? ''}
                  InputProps={{ readOnly: true }}
                  disabled
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Peso Gap Tecnologico vs BATs"
                  type="number"
                  value={settingsFormData.weight_technological_gap_vs_bats ?? ''}
                  InputProps={{ readOnly: true }}
                  disabled
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Peso Gestione Impianti"
                  type="number"
                  value={settingsFormData.weight_plant_management ?? ''}
                  InputProps={{ readOnly: true }}
                  disabled
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Conoscenza / Misurazione"
                  type="number"
                  value={settingsFormData.knowledge_measurement ?? ''}
                  InputProps={{ readOnly: true }}
                  disabled
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Soglia Minima"
                  type="number"
                  value={settingsFormData.minimum_threshold ?? ''}
                  InputProps={{ readOnly: true }}
                  disabled
                />
              </Grid>
              <Grid size={{ xs: 12, md: 12 }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveSettings}
                  disabled={updateSettingsMutation.isLoading}
                >
                  Salva
                </Button>
              </Grid>
            </Grid>
          ) : (
            <Typography color="text.secondary">Impostazioni non disponibili</Typography>
          )}
        </TabPanel>
        <EditEnergyUseDialog
          open={editDialogOpen}
          onClose={handleCloseEditDialog}
          energyUse={selectedEnergyUse}
          diagnosisId={diagnosisId}
        />
        <AiSearchModal
          open={aiSearchOpen}
          onClose={() => setAiSearchOpen(false)}
          fixedEntityType="energy_use"
          diagnosisId={diagnosisId}
          onInsert={async item => {
            if (!diagnosisId) return;
            const row = await resolveAiEnergyUseRow(item, apiClient);
            await handleImportAiEnergyUse(item, row);
          }}
        />
        <ActionSpacer></ActionSpacer>
      </Stack>
    );
  })
);
